/**
 * Company Notes API Service
 * 
 * API client for the tabbed company research notes system.
 * Each company has its own notebook with multiple organized tabs.
 */
import { api } from '../auth/apiClient';

const BASE = '/notes/company';

/**
 * Fetch full company notes (all tabs + content)
 */
export const getCompanyNotes = async (companyId) => {
    const res = await api.get(`${BASE}/${companyId}`);
    return res;
};

/**
 * Fetch lightweight summary (badge count)
 */
export const getNoteSummary = async (companyId) => {
    const res = await api.get(`${BASE}/${companyId}?mode=summary`);
    return res;
};

/**
 * Auto-save tab content (PATCH)
 */
export const updateTabContent = async (companyId, tabId, { title, richContent, plainText, companyName }) => {
    const res = await api.patch(`${BASE}/${companyId}/tab/${tabId}`, {
        title,
        richContent,
        plainText,
        companyName,
    });
    return res;
};

/**
 * Create a new custom tab
 */
export const createTab = async (companyId, { tabName, order, companyName }) => {
    const res = await api.post(`${BASE}/${companyId}/tabs`, {
        tabName,
        order,
        companyName,
    });
    return res;
};

/**
 * Delete a custom tab (default tabs cannot be deleted)
 */
export const deleteTab = async (companyId, tabId) => {
    const res = await api.delete(`${BASE}/${companyId}/tabs/${tabId}`);
    return res;
};

/**
 * Reorder tabs
 */
export const reorderTabs = async (companyId, tabOrder) => {
    const res = await api.put(`${BASE}/${companyId}/tabs/reorder`, { tabOrder });
    return res;
};

/**
 * Save an AI response into a specific tab
 */
export const saveFromAI = async (companyId, { tabId, content, appendMode = true, aiContext, companyName }) => {
    const res = await api.post(`${BASE}/${companyId}/save-from-ai`, {
        tabId,
        content,
        appendMode,
        aiContext,
        companyName,
    });
    return res;
};

/* ── Image Upload APIs ─────────────────────────────────── */

/**
 * Get a SAS upload token for direct browser-to-blob upload
 */
export const getImageUploadToken = async (companyId, { fileName, fileSize, mimeType, tabId }) => {
    const res = await api.post(`${BASE}/${companyId}/images/upload-token`, {
        fileName, fileSize, mimeType, tabId,
    });
    return res;
};

/**
 * Confirm an image upload after the blob PUT succeeds
 */
export const confirmImageUpload = async (companyId, attachmentId) => {
    const res = await api.post(`${BASE}/${companyId}/images/${attachmentId}/confirm`);
    return res;
};

/**
 * Delete an image attachment
 */
export const deleteImage = async (companyId, attachmentId) => {
    const res = await api.delete(`${BASE}/${companyId}/images/${attachmentId}`);
    return res;
};

/**
 * List all active images for a company
 */
export const listImages = async (companyId) => {
    const res = await api.get(`${BASE}/${companyId}/images`);
    return res;
};
